/**
 * 驻场作家前端客户端（P1）。
 * REST + SSE 都走同源 Cookie；手机设备令牌在 P3 接。
 */

import type { HarnessEvent } from '@xiaotianshu/shared/harness';

export interface AgentBook {
  id: string;
  title: string;
  wordCount: number;
  updatedAt: string | null;
  pendingIdeas: number;
}

export interface AgentPersona {
  id: string;
  name: string;
  nickname: string | null;
  identity: string;
  voice: string;
  aesthetics: string;
  boundaries: string;
  style_profile: Record<string, unknown>;
  version: number;
  is_default: boolean;
}

export interface AgentThread {
  id: string;
  bookId: string;
  personaId: string;
  personaName: string;
  title: string;
  harnessSessionId: string | null;
  provider: string;
  model: string;
  epoch?: number;
  lastContextTokens?: number;
  updatedAt: string | null;
}

export interface AgentIdea {
  id: string;
  book_id: string;
  thread_id: string | null;
  kind: string;
  target_chapter_id: string | null;
  title: string;
  summary: string;
  content: string;
  rationale: string;
  status: 'proposed' | 'approved' | 'drafting' | 'rejected' | 'applied';
  created_at: string | null;
}

export interface AgentDraft {
  id: string;
  book_id: string;
  idea_id: string | null;
  chapter_id: string | null;
  title: string | null;
  summary: string;
  content: string;
  original_content?: string | null;
  word_count: number;
  status: 'pending' | 'accepted' | 'rejected' | 'preview';
  review_comment: string | null;
  created_at: string | null;
}

export interface AgentDocument {
  name: string;
  text: string;
  mimeType?: string;
  dataUrl?: string;
  size?: number;
}

export interface AgentImage {
  dataUrl: string;
  mimeType: string;
  name?: string;
}

export interface AgentMessage {
  id: string;
  role: 'user' | 'assistant' | 'tool' | 'system';
  text: string;
  images?: AgentImage[];
  documents?: AgentDocument[];
  shared?: boolean;
  created_at: string | null;
}

export interface AgentMemory {
  id: string;
  kind: string;
  content: string;
  tags: string[];
  salience: number;
  source: string;
  created_at: string | null;
}

export interface PairingCode {
  code: string;
  expiresAt: string;
  scopes: string[];
}

export interface BudgetStatus {
  dailyLimit: number;
  monthlyLimit: number;
  dailySpent: number;
  monthlySpent: number;
  autonomy: number;
  active: boolean;
  freeEdit: boolean;
  fullAccess: boolean;
  blocked: boolean;
  reason?: string;
}

export interface AgentObservation {
  id: string;
  sourceUrl: string;
  sourceTitle: string;
  excerpt: string;
  note: string;
  relevance: number;
  createdAt: string | null;
}

async function jsonOrThrow<T>(res: Response): Promise<T> {
  const text = await res.text();
  let data: unknown = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = text; }
  if (!res.ok) {
    const message = data && typeof data === 'object' && 'error' in data
      ? String((data as { error: unknown }).error)
      : `HTTP ${res.status}`;
    throw new Error(message);
  }
  return data as T;
}

export async function listAgentBooks(): Promise<AgentBook[]> {
  const res = await fetch('/api/agent/books', { credentials: 'include' });
  return (await jsonOrThrow<{ books: AgentBook[] }>(res)).books;
}

export async function listPersonas(): Promise<AgentPersona[]> {
  const res = await fetch('/api/agent/personas', { credentials: 'include' });
  return (await jsonOrThrow<{ personas: AgentPersona[] }>(res)).personas;
}

export async function updatePersona(id: string, patch: Partial<AgentPersona>): Promise<AgentPersona> {
  const res = await fetch(`/api/agent/personas/${encodeURIComponent(id)}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  return (await jsonOrThrow<{ persona: AgentPersona }>(res)).persona;
}

export async function getBookStyle(bookId: string): Promise<Record<string, unknown>> {
  const res = await fetch(`/api/agent/books/${encodeURIComponent(bookId)}/style`, { credentials: 'include' });
  return (await jsonOrThrow<{ styleProfile: Record<string, unknown> }>(res)).styleProfile;
}

export async function updateBookStyle(bookId: string, style: Record<string, unknown>): Promise<Record<string, unknown>> {
  const res = await fetch(`/api/agent/books/${encodeURIComponent(bookId)}/style`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ style_profile: style }),
  });
  return (await jsonOrThrow<{ styleProfile: Record<string, unknown> }>(res)).styleProfile;
}

export interface StyleAnalysis {
  profile: Record<string, unknown>;
  stats: {
    chapterCount: number;
    sampleChars: number;
    avgParagraphChars: number;
    avgSentenceChars: number;
  };
  notes: string[];
}

export async function analyzeBookStyle(bookId: string): Promise<StyleAnalysis> {
  const res = await fetch(`/api/agent/books/${encodeURIComponent(bookId)}/style/analyze`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({}),
  });
  return jsonOrThrow<StyleAnalysis>(res);
}

export async function ensureThread(bookId: string): Promise<AgentThread> {  const res = await fetch('/api/agent/threads', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ bookId }),
  });
  return (await jsonOrThrow<{ thread: AgentThread }>(res)).thread;
}

export async function listThreads(bookId: string): Promise<AgentThread[]> {
  const res = await fetch(`/api/agent/threads?bookId=${encodeURIComponent(bookId)}`, { credentials: 'include' });
  return (await jsonOrThrow<{ threads: AgentThread[] }>(res)).threads;
}

/** 保留历史，用最新人格/文风重开会话（页面随后刷新以重新订阅）。 */
export async function reloadThread(threadId: string): Promise<AgentThread> {
  const res = await fetch(`/api/agent/threads/${encodeURIComponent(threadId)}/reload`, {
    method: 'POST', credentials: 'include',
  });
  return (await jsonOrThrow<{ thread: AgentThread }>(res)).thread;
}

export async function getThreadMessages(threadId: string): Promise<AgentMessage[]> {
  const res = await fetch(`/api/agent/threads/${encodeURIComponent(threadId)}/messages`, { credentials: 'include' });
  return (await jsonOrThrow<{ messages: AgentMessage[] }>(res)).messages;
}

export async function sendAgentMessage(
  threadId: string,
  text: string,
  images?: AgentImage[],
  documents?: AgentDocument[],
): Promise<void> {
  const res = await fetch(`/api/agent/threads/${encodeURIComponent(threadId)}/messages`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      text,
      ...(images && images.length > 0 ? { images } : {}),
      ...(documents && documents.length > 0 ? { documents } : {}),
    }),
  });
  await jsonOrThrow(res);
}

export async function answerAgentApproval(
  threadId: string,
  requestId: string,
  decision: 'allow' | 'deny',
): Promise<void> {
  const res = await fetch(`/api/agent/threads/${encodeURIComponent(threadId)}/approvals/${encodeURIComponent(requestId)}`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ decision }),
  });
  await jsonOrThrow(res);
}

export async function abortAgentTurn(threadId: string): Promise<void> {  const res = await fetch(`/api/agent/threads/${encodeURIComponent(threadId)}/abort`, {
    method: 'POST', credentials: 'include',
  });
  await jsonOrThrow(res);
}

export async function listIdeas(bookId: string, status?: string): Promise<AgentIdea[]> {
  const qs = new URLSearchParams({ bookId });
  if (status) qs.set('status', status);
  const res = await fetch(`/api/agent/ideas?${qs.toString()}`, { credentials: 'include' });
  return (await jsonOrThrow<{ ideas: AgentIdea[] }>(res)).ideas;
}

export async function createIdea(input: {
  bookId: string;
  title: string;
  summary?: string;
  content?: string;
  rationale?: string;
  kind?: 'new_chapter' | 'revision' | 'world' | 'other';
  threadId?: string;
  personaId?: string;
  targetChapterId?: string;
}): Promise<AgentIdea> {
  const res = await fetch('/api/agent/ideas', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  return (await jsonOrThrow<{ idea: AgentIdea }>(res)).idea;
}

export async function approveIdea(id: string): Promise<AgentIdea> {
  const res = await fetch(`/api/agent/ideas/${encodeURIComponent(id)}/approve`, {
    method: 'POST', credentials: 'include',
  });
  return (await jsonOrThrow<{ idea: AgentIdea }>(res)).idea;
}

export async function deleteIdea(id: string): Promise<void> {
  const res = await fetch(`/api/agent/ideas/${encodeURIComponent(id)}`, {
    method: 'DELETE', credentials: 'include',
  });
  await jsonOrThrow(res);
}

export async function rejectIdea(id: string, reason?: string): Promise<AgentIdea> {  const res = await fetch(`/api/agent/ideas/${encodeURIComponent(id)}/reject`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason }),
  });
  return (await jsonOrThrow<{ idea: AgentIdea }>(res)).idea;
}

export async function listDrafts(bookId: string, status?: string): Promise<AgentDraft[]> {
  const qs = new URLSearchParams({ bookId });
  if (status) qs.set('status', status);
  const res = await fetch(`/api/agent/drafts?${qs.toString()}`, { credentials: 'include' });
  return (await jsonOrThrow<{ drafts: AgentDraft[] }>(res)).drafts;
}

export async function updateDraft(
  id: string,
  patch: { title?: string; summary?: string; content?: string },
): Promise<AgentDraft> {
  const res = await fetch(`/api/agent/drafts/${encodeURIComponent(id)}`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  return (await jsonOrThrow<{ draft: AgentDraft }>(res)).draft;
}

export async function acceptDraft(id: string): Promise<{ chapterId: string }> {  const res = await fetch(`/api/agent/drafts/${encodeURIComponent(id)}/accept`, {
    method: 'POST', credentials: 'include',
  });
  return jsonOrThrow<{ chapterId: string }>(res);
}

export async function deleteDraft(id: string): Promise<void> {
  const res = await fetch(`/api/agent/drafts/${encodeURIComponent(id)}`, {
    method: 'DELETE', credentials: 'include',
  });
  await jsonOrThrow(res);
}

export async function rejectDraft(id: string, comment?: string): Promise<AgentDraft> {
  const res = await fetch(`/api/agent/drafts/${encodeURIComponent(id)}/reject`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ comment }),
  });
  return (await jsonOrThrow<{ draft: AgentDraft }>(res)).draft;
}

export async function createPairingCode(): Promise<PairingCode> {
  const res = await fetch('/api/agent/pairing/code', { method: 'POST', credentials: 'include' });
  return jsonOrThrow<PairingCode>(res);
}

export async function getAgentBudget(bookId: string): Promise<BudgetStatus> {
  const res = await fetch(`/api/agent/books/${encodeURIComponent(bookId)}/budget`, { credentials: 'include' });
  return jsonOrThrow<BudgetStatus>(res);
}

export async function readWeb(bookId: string, url: string, note = ''): Promise<AgentObservation> {
  const res = await fetch(`/api/agent/books/${encodeURIComponent(bookId)}/observe`, {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ url, note }),
  });
  return (await jsonOrThrow<{ observation: AgentObservation }>(res)).observation;
}

export async function listObservations(bookId: string): Promise<AgentObservation[]> {
  const res = await fetch(`/api/agent/books/${encodeURIComponent(bookId)}/observations`, { credentials: 'include' });
  return (await jsonOrThrow<{ observations: AgentObservation[] }>(res)).observations;
}

export async function triggerProactive(bookId: string): Promise<{ threadId: string; chapterId: string }> {  const res = await fetch(`/api/agent/books/${encodeURIComponent(bookId)}/proactive`, {
    method: 'POST', credentials: 'include',
  });
  return jsonOrThrow<{ threadId: string; chapterId: string }>(res);
}

export async function updateAgentBudget(  bookId: string,
  patch: { dailyBudgetCny?: number; monthlyBudgetCny?: number; autonomy?: number; active?: boolean; freeEdit?: boolean; fullAccess?: boolean },
): Promise<BudgetStatus> {
  const res = await fetch(`/api/agent/books/${encodeURIComponent(bookId)}/budget`, {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  });
  return jsonOrThrow<BudgetStatus>(res);
}

export async function listAgentMemories(bookId: string, q = ''): Promise<AgentMemory[]> {  const qs = new URLSearchParams({ bookId });
  if (q.trim()) qs.set('q', q.trim());
  const res = await fetch(`/api/agent/memories?${qs.toString()}`, { credentials: 'include' });
  return (await jsonOrThrow<{ memories: AgentMemory[] }>(res)).memories;
}

export async function createAgentMemory(
  bookId: string,
  input: { kind: string; content: string; salience?: number },
): Promise<AgentMemory> {
  const res = await fetch('/api/agent/memories', {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ bookId, ...input }),
  });
  return (await jsonOrThrow<{ memory: AgentMemory }>(res)).memory;
}

export async function archiveAgentMemory(id: string): Promise<void> {
  const res = await fetch(`/api/agent/memories/${encodeURIComponent(id)}`, {
    method: 'DELETE', credentials: 'include',
  });
  await jsonOrThrow(res);
}

/** 订阅线程事件；返回关闭函数。 */
export function openAgentStream(
  threadId: string,
  onEvent: (event: HarnessEvent) => void,
  onError?: (message: string) => void,
  options?: { replay?: boolean },
): () => void {
  const replay = options?.replay === false ? '0' : '1';
  const es = new EventSource(`/api/agent/threads/${encodeURIComponent(threadId)}/events?replay=${replay}`);
  es.addEventListener('agent', (ev) => {
    try {
      onEvent(JSON.parse((ev as MessageEvent).data) as HarnessEvent);
    } catch {
      /* ignore malformed frame */
    }
  });
  es.onerror = () => {
    // EventSource 会自动重连；这里只把状态报给 UI，不 close。
    onError?.('连接中断，正在重连…');
  };
  return () => es.close();
}
